// src/stock/AjouterProduit.jsx
import React, { useState, useEffect } from "react";
import {
    Card, Row, Col, Form, Button, Spinner, InputGroup
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   ÉTAT INITIAL
   ============================================================ */
const INITIAL_STATE = {
    type_article: 'produit',
    reference: '',
    code_barre: '',
    designation: '',
    description: '',
    id_categorie: '',
    unite_mesure: 'PCE',
    unite_facturation: 'FORFAIT',
    prix_achat_ht: 0,
    prix_vente_ht: 0,
    tva_taux: 18,
    css_taux: 1,
    seuil_alerte: 5,
    stock_minimum: 0,
    stock_maximum: '',
    emplacement: '',
    actif: 1
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function AjouterProduit() {
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });
    const [produit, setProduit] = useState({ ...INITIAL_STATE });

    const isService = produit.type_article === 'service';

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    useEffect(() => { fetchCategories(); }, []);

    /* ============================================================
       FETCH CATÉGORIES
       ============================================================ */
    const fetchCategories = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/categories.php`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setCategories(result.data || []);
            }
        } catch (err) {
            console.error("Erreur chargement catégories", err);
        }
    };

    /* ============================================================
       HANDLERS
       ============================================================ */
    const resetForm = () => setProduit({ ...INITIAL_STATE });

    const handleTypeChange = (newType) => {
        setProduit(prev => ({
            ...prev,
            type_article: newType,
            ...(newType === 'service'
                ? { prix_achat_ht: 0, seuil_alerte: 5, stock_minimum: 0, stock_maximum: '', emplacement: '' }
                : { unite_facturation: 'FORFAIT' })
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!produit.designation) {
            showToast('error', 'Champ requis', "La désignation est obligatoire");
            return;
        }
        if (!produit.id_categorie) {
            showToast('error', 'Champ requis', "La catégorie est obligatoire");
            return;
        }
        if (!produit.prix_vente_ht || produit.prix_vente_ht <= 0) {
            showToast('error', 'Champ invalide',
                isService
                    ? "Le montant de la prestation doit être supérieur à 0"
                    : "Le prix de vente doit être supérieur à 0");
            return;
        }
        if (!isService && (!produit.prix_achat_ht || produit.prix_achat_ht <= 0)) {
            showToast('error', 'Champ invalide', "Le prix d'achat doit être supérieur à 0");
            return;
        }

        const payload = { ...produit };
        if (isService) {
            payload.unite_mesure  = null;
            payload.seuil_alerte  = null;
            payload.stock_minimum = null;
            payload.stock_maximum = null;
            payload.emplacement   = null;
            payload.prix_achat_ht = 0;
        } else {
            payload.unite_facturation = null;
        }

        setSaving(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/produits.php`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                }
            );

            const result = await response.json();
            if (result.success) {
                showToast('success',
                    isService ? 'Service créé' : 'Produit créé',
                    'Enregistrement réussi ! Redirection...');
                setTimeout(() => navigate('/stock/produits'), 1500);
            } else {
                showToast('error', 'Erreur', result.message || "Erreur lors de la création");
            }
        } catch (err) {
            showToast('error', 'Erreur de connexion', "Impossible de contacter le serveur");
        } finally {
            setSaving(false);
        }
    };

    /* ============================================================
       OPTIONS
       ============================================================ */
    const uniteOptions = [
        { value: 'PCE',  label: 'Pièce' },
        { value: 'KG',   label: 'Kilogramme' },
        { value: 'L',    label: 'Litre' },
        { value: 'M',    label: 'Mètre' },
        { value: 'CART', label: 'Carton' },
        { value: 'PAL',  label: 'Palette' }
    ];

    const uniteFacturationOptions = [
        { value: 'FORFAIT',      label: 'Forfait global' },
        { value: 'JOUR',         label: 'Jour' },
        { value: 'HEURE',        label: 'Heure' },
        { value: 'MOIS',         label: 'Mois' },
        { value: 'PRESTATION',   label: 'Prestation' },
        { value: 'INTERVENTION', label: 'Intervention' }
    ];

    /* ============================================================
       RENDU
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className={isService ? "ri-service-line" : "ri-add-line"}></i>
                            </div>
                            <div>
                                <h5 className="header-title">
                                    {isService ? 'Ajouter un service' : 'Ajouter un produit'}
                                </h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className={isService ? "ri-service-line" : "ri-add-circle-line"}></i>
                                        {isService
                                            ? 'Créer une prestation non quantifiable'
                                            : 'Créer un nouveau produit dans le catalogue'}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={() => navigate('/stock/produits')}>
                                <i className="ri-arrow-left-line"></i> Retour
                            </Button>
                            {!isService && (
                                <Button className="btn-modern-outline" onClick={() => navigate('/stock/produits/import')}>
                                    <i className="ri-upload-cloud-2-line"></i> Importer
                                </Button>
                            )}
                        </div>
                    </div>

                    <Row className="justify-content-center">
                        <Col xl={11} lg={12}>
                            <Card className="border-0 shadow-sm rounded-4 form-card">
                                <Card.Header className="card-header-gradient">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <h5 className="mb-0 text-white d-flex align-items-center">
                                            <i className="ri-information-line me-2"></i>
                                            {isService ? 'Informations du service' : 'Informations du produit'}
                                        </h5>
                                        <span className="header-pill">
                                            <i className="ri-time-line"></i> Saisie rapide
                                        </span>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-4 p-lg-5">
                                    <Form onSubmit={handleSubmit} className="fade-in-form">
                                        <Row className="g-4">

                                            {/* ============ SÉLECTEUR DE TYPE ============ */}
                                            <Col xs={12}>
                                                <div className="section-title-charte">
                                                    <span className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                                        <i className="ri-list-check-2"></i>
                                                    </span>
                                                    <h6>Type d'article</h6>
                                                </div>
                                                <div className="type-selector-charte">
                                                    <button
                                                        type="button"
                                                        className={`type-btn ${!isService ? 'active' : ''}`}
                                                        onClick={() => handleTypeChange('produit')}
                                                    >
                                                        <div className="type-btn-icon">
                                                            <i className="ri-shopping-cart-line"></i>
                                                        </div>
                                                        <div className="type-btn-content">
                                                            <span>Produit</span>
                                                            <small>Article stockable, quantité et stock</small>
                                                        </div>
                                                        {!isService && <i className="ri-checkbox-circle-fill type-btn-check"></i>}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`type-btn ${isService ? 'active' : ''}`}
                                                        onClick={() => handleTypeChange('service')}
                                                    >
                                                        <div className="type-btn-icon">
                                                            <i className="ri-service-line"></i>
                                                        </div>
                                                        <div className="type-btn-content">
                                                            <span>Service</span>
                                                            <small>Prestation non quantifiable, montant global</small>
                                                        </div>
                                                        {isService && <i className="ri-checkbox-circle-fill type-btn-check"></i>}
                                                    </button>
                                                </div>
                                            </Col>

                                            {/* ============ SECTION 1 : Informations de base ============ */}
                                            <Col xs={12}>
                                                <div className="section-block">
                                                    <div className="section-title-charte">
                                                        <span className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                                            <i className="ri-information-line"></i>
                                                        </span>
                                                        <h6>Informations de base</h6>
                                                    </div>

                                                    <Row className="g-3">
                                                        <Col xs={12} md={4}>
                                                            <Form.Group>
                                                                <Form.Label className="form-label-modern">Référence</Form.Label>
                                                                <InputGroup className="input-modern">
                                                                    <InputGroup.Text><i className="ri-barcode-line"></i></InputGroup.Text>
                                                                    <Form.Control
                                                                        type="text"
                                                                        value={produit.reference}
                                                                        onChange={(e) => setProduit({...produit, reference: e.target.value})}
                                                                        placeholder="Auto si vide"
                                                                    />
                                                                </InputGroup>
                                                            </Form.Group>
                                                        </Col>

                                                        <Col xs={12} md={4}>
                                                            <Form.Group>
                                                                <Form.Label className="form-label-modern">
                                                                    {isService ? 'Code prestation' : 'Code-barres'}
                                                                </Form.Label>
                                                                <InputGroup className="input-modern">
                                                                    <InputGroup.Text><i className="ri-qr-code-line"></i></InputGroup.Text>
                                                                    <Form.Control
                                                                        type="text"
                                                                        value={produit.code_barre}
                                                                        onChange={(e) => setProduit({...produit, code_barre: e.target.value})}
                                                                        placeholder="Optionnel"
                                                                    />
                                                                </InputGroup>
                                                            </Form.Group>
                                                        </Col>

                                                        <Col xs={12} md={4}>
                                                            <Form.Group>
                                                                <Form.Label className="form-label-modern">
                                                                    Catégorie <span className="text-danger">*</span>
                                                                </Form.Label>
                                                                <InputGroup className="input-modern">
                                                                    <InputGroup.Text><i className="ri-price-tag-3-line"></i></InputGroup.Text>
                                                                    <Form.Select
                                                                        value={produit.id_categorie}
                                                                        onChange={(e) => setProduit({...produit, id_categorie: e.target.value})}
                                                                        required
                                                                    >
                                                                        <option value="">Sélectionner une catégorie</option>
                                                                        {categories.map(cat => (
                                                                            <option key={cat.id_categorie} value={cat.id_categorie}>
                                                                                {cat.nom_categorie}
                                                                            </option>
                                                                        ))}
                                                                    </Form.Select>
                                                                </InputGroup>
                                                            </Form.Group>
                                                        </Col>

                                                        <Col xs={12} md={8}>
                                                            <Form.Group>
                                                                <Form.Label className="form-label-modern">
                                                                    Désignation <span className="text-danger">*</span>
                                                                </Form.Label>
                                                                <InputGroup className="input-modern">
                                                                    <InputGroup.Text><i className="ri-text"></i></InputGroup.Text>
                                                                    <Form.Control
                                                                        type="text"
                                                                        value={produit.designation}
                                                                        onChange={(e) => setProduit({...produit, designation: e.target.value})}
                                                                        required
                                                                        placeholder={isService ? "Nom de la prestation" : "Nom du produit"}
                                                                    />
                                                                </InputGroup>
                                                            </Form.Group>
                                                        </Col>

                                                        <Col xs={12} md={4}>
                                                            <Form.Group>
                                                                <Form.Label className="form-label-modern">
                                                                    {isService ? 'Unité de facturation' : 'Unité de mesure'}
                                                                </Form.Label>
                                                                <InputGroup className="input-modern">
                                                                    <InputGroup.Text><i className="ri-scales-line"></i></InputGroup.Text>
                                                                    {isService ? (
                                                                        <Form.Select
                                                                            value={produit.unite_facturation}
                                                                            onChange={(e) => setProduit({...produit, unite_facturation: e.target.value})}
                                                                        >
                                                                            {uniteFacturationOptions.map(opt => (
                                                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                                            ))}
                                                                        </Form.Select>
                                                                    ) : (
                                                                        <Form.Select
                                                                            value={produit.unite_mesure}
                                                                            onChange={(e) => setProduit({...produit, unite_mesure: e.target.value})}
                                                                        >
                                                                            {uniteOptions.map(opt => (
                                                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                                            ))}
                                                                        </Form.Select>
                                                                    )}
                                                                </InputGroup>
                                                            </Form.Group>
                                                        </Col>

                                                        <Col xs={12}>
                                                            <Form.Group>
                                                                <Form.Label className="form-label-modern">Description</Form.Label>
                                                                <InputGroup className="input-modern">
                                                                    <InputGroup.Text className="align-items-start pt-2"><i className="ri-file-text-line"></i></InputGroup.Text>
                                                                    <Form.Control
                                                                        as="textarea"
                                                                        rows={3}
                                                                        value={produit.description}
                                                                        onChange={(e) => setProduit({...produit, description: e.target.value})}
                                                                        placeholder={isService ? "Description de la prestation..." : "Description du produit..."}
                                                                    />
                                                                </InputGroup>
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>
                                                </div>
                                            </Col>

                                            {/* ============ SECTION 2 : Prix / Montant et taxes ============ */}
                                            <Col xs={12}>
                                                <div className="section-block">
                                                    <div className="section-title-charte">
                                                        <span className="section-icon-charte" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                                            <i className="ri-money-euro-circle-line"></i>
                                                        </span>
                                                        <h6>{isService ? 'Montant et taxes' : 'Prix et taxes'}</h6>
                                                    </div>

                                                    <Row className="g-3">
                                                        {!isService && (
                                                            <Col xs={12} md={4}>
                                                                <Form.Group>
                                                                    <Form.Label className="form-label-modern">
                                                                        Prix d'achat HT <span className="text-danger">*</span>
                                                                    </Form.Label>
                                                                    <InputGroup className="input-modern">
                                                                        <InputGroup.Text><i className="ri-shopping-cart-line"></i></InputGroup.Text>
                                                                        <Form.Control
                                                                            type="number"
                                                                            step="0.01"
                                                                            min="0"
                                                                            value={produit.prix_achat_ht}
                                                                            onChange={(e) => setProduit({...produit, prix_achat_ht: parseFloat(e.target.value) || 0})}
                                                                            required={!isService}
                                                                        />
                                                                        <InputGroup.Text className="bg-light">FCFA</InputGroup.Text>
                                                                    </InputGroup>
                                                                </Form.Group>
                                                            </Col>
                                                        )}

                                                        <Col xs={12} md={isService ? 6 : 4}>
                                                            <Form.Group>
                                                                <Form.Label className="form-label-modern">
                                                                    {isService ? 'Montant HT' : 'Prix de vente HT'}{' '}
                                                                    <span className="text-danger">*</span>
                                                                </Form.Label>
                                                                <InputGroup className="input-modern">
                                                                    <InputGroup.Text><i className="ri-money-dollar-circle-line"></i></InputGroup.Text>
                                                                    <Form.Control
                                                                        type="number"
                                                                        step="0.01"
                                                                        min="0"
                                                                        value={produit.prix_vente_ht}
                                                                        onChange={(e) => setProduit({...produit, prix_vente_ht: parseFloat(e.target.value) || 0})}
                                                                        required
                                                                    />
                                                                    <InputGroup.Text className="bg-light">FCFA</InputGroup.Text>
                                                                </InputGroup>
                                                            </Form.Group>
                                                        </Col>

                                                        <Col xs={12} md={isService ? 3 : 4}>
                                                            <Form.Group>
                                                                <Form.Label className="form-label-modern">TVA (%)</Form.Label>
                                                                <InputGroup className="input-modern">
                                                                    <InputGroup.Text><i className="ri-percent-line"></i></InputGroup.Text>
                                                                    <Form.Control
                                                                        type="number"
                                                                        step="0.1"
                                                                        min="0"
                                                                        max="100"
                                                                        value={produit.tva_taux}
                                                                        onChange={(e) => setProduit({...produit, tva_taux: parseFloat(e.target.value) || 0})}
                                                                    />
                                                                    <InputGroup.Text className="bg-light">%</InputGroup.Text>
                                                                </InputGroup>
                                                            </Form.Group>
                                                        </Col>

                                                        <Col xs={12} md={isService ? 3 : 4}>
                                                            <Form.Group>
                                                                <Form.Label className="form-label-modern">CSS (%)</Form.Label>
                                                                <InputGroup className="input-modern">
                                                                    <InputGroup.Text><i className="ri-percent-line"></i></InputGroup.Text>
                                                                    <Form.Control
                                                                        type="number"
                                                                        step="0.1"
                                                                        min="0"
                                                                        max="100"
                                                                        value={produit.css_taux}
                                                                        onChange={(e) => setProduit({...produit, css_taux: parseFloat(e.target.value) || 0})}
                                                                    />
                                                                    <InputGroup.Text className="bg-light">%</InputGroup.Text>
                                                                </InputGroup>
                                                            </Form.Group>
                                                        </Col>

                                                        {!isService && (
                                                            <Col xs={12} md={4}>
                                                                <Form.Group>
                                                                    <Form.Label className="form-label-modern">Marge estimée</Form.Label>
                                                                    <div className="marge-box-charte">
                                                                        {produit.prix_achat_ht > 0 ? (
                                                                            <span className="fw-bold" style={{
                                                                                color: (produit.prix_vente_ht - produit.prix_achat_ht) >= 0 ? COLORS.success : COLORS.danger
                                                                            }}>
                                                                                {((produit.prix_vente_ht - produit.prix_achat_ht) / produit.prix_achat_ht * 100).toFixed(1)}%
                                                                            </span>
                                                                        ) : (
                                                                            <span className="text-muted">—</span>
                                                                        )}
                                                                    </div>
                                                                </Form.Group>
                                                            </Col>
                                                        )}
                                                    </Row>
                                                </div>
                                            </Col>

                                            {/* ============ SECTION 3 : Gestion de stock ============ */}
                                            {!isService && (
                                                <Col xs={12}>
                                                    <div className="section-block">
                                                        <div className="section-title-charte">
                                                            <span className="section-icon-charte" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                                                <i className="ri-stack-line"></i>
                                                            </span>
                                                            <h6>Gestion de stock</h6>
                                                        </div>

                                                        <Row className="g-3">
                                                            <Col xs={12} md={3}>
                                                                <Form.Group>
                                                                    <Form.Label className="form-label-modern">Seuil d'alerte</Form.Label>
                                                                    <InputGroup className="input-modern">
                                                                        <InputGroup.Text><i className="ri-alert-line"></i></InputGroup.Text>
                                                                        <Form.Control
                                                                            type="number"
                                                                            min="0"
                                                                            value={produit.seuil_alerte}
                                                                            onChange={(e) => setProduit({...produit, seuil_alerte: parseInt(e.target.value) || 0})}
                                                                        />
                                                                        <InputGroup.Text className="bg-light">u</InputGroup.Text>
                                                                    </InputGroup>
                                                                </Form.Group>
                                                            </Col>

                                                            <Col xs={12} md={3}>
                                                                <Form.Group>
                                                                    <Form.Label className="form-label-modern">Stock minimum</Form.Label>
                                                                    <InputGroup className="input-modern">
                                                                        <InputGroup.Text><i className="ri-arrow-down-line"></i></InputGroup.Text>
                                                                        <Form.Control
                                                                            type="number"
                                                                            min="0"
                                                                            value={produit.stock_minimum}
                                                                            onChange={(e) => setProduit({...produit, stock_minimum: parseInt(e.target.value) || 0})}
                                                                        />
                                                                        <InputGroup.Text className="bg-light">u</InputGroup.Text>
                                                                    </InputGroup>
                                                                </Form.Group>
                                                            </Col>

                                                            <Col xs={12} md={3}>
                                                                <Form.Group>
                                                                    <Form.Label className="form-label-modern">Stock maximum</Form.Label>
                                                                    <InputGroup className="input-modern">
                                                                        <InputGroup.Text><i className="ri-arrow-up-line"></i></InputGroup.Text>
                                                                        <Form.Control
                                                                            type="number"
                                                                            min="0"
                                                                            value={produit.stock_maximum}
                                                                            onChange={(e) => setProduit({...produit, stock_maximum: parseInt(e.target.value) || ''})}
                                                                        />
                                                                        <InputGroup.Text className="bg-light">u</InputGroup.Text>
                                                                    </InputGroup>
                                                                </Form.Group>
                                                            </Col>

                                                            <Col xs={12} md={3}>
                                                                <Form.Group>
                                                                    <Form.Label className="form-label-modern">Emplacement</Form.Label>
                                                                    <InputGroup className="input-modern">
                                                                        <InputGroup.Text><i className="ri-map-pin-line"></i></InputGroup.Text>
                                                                        <Form.Control
                                                                            type="text"
                                                                            value={produit.emplacement}
                                                                            onChange={(e) => setProduit({...produit, emplacement: e.target.value})}
                                                                            placeholder="Ex: A-12-3"
                                                                        />
                                                                    </InputGroup>
                                                                </Form.Group>
                                                            </Col>
                                                        </Row>
                                                    </div>
                                                </Col>
                                            )}

                                            {/* ============ SECTION 3bis : Bloc info service ============ */}
                                            {isService && (
                                                <Col xs={12}>
                                                    <div className="service-info-box">
                                                        <i className="ri-information-line"></i>
                                                        <div>
                                                            <strong>Service non quantifiable</strong>
                                                            <p className="mb-0 small text-muted">
                                                                Ce service n'est pas soumis à la gestion de stock. Il sera facturé
                                                                au montant HT défini ci-dessus, avec la TVA et le CSS appliqués
                                                                automatiquement. L'unité de facturation sert uniquement
                                                                à préciser le mode de tarification (forfait, jour, heure…).
                                                            </p>
                                                        </div>
                                                    </div>
                                                </Col>
                                            )}

                                            {/* ============ SECTION 4 : Actions ============ */}
                                            <Col xs={12}>
                                                <div className="action-bar">
                                                    <Button
                                                        type="button"
                                                        className="btn-modern-outline"
                                                        onClick={resetForm}
                                                    >
                                                        <i className="ri-refresh-line me-1"></i> Réinitialiser
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        className="btn-modern-outline"
                                                        onClick={() => navigate('/stock/produits')}
                                                    >
                                                        Annuler
                                                    </Button>
                                                    <Button
                                                        type="submit"
                                                        className="btn-modern-primary"
                                                        disabled={saving}
                                                    >
                                                        {saving ? (
                                                            <><Spinner size="sm" className="me-2" />Création...</>
                                                        ) : (
                                                            <>
                                                                <i className="ri-save-line me-2"></i>
                                                                {isService ? 'Créer le service' : 'Créer le produit'}
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Form>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Footer />
            </div>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 14px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 56px; height: 56px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 16px; color: white; font-size: 1.6rem;
                    box-shadow: 0 6px 16px rgba(41, 90, 167, 0.28);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }

                /* ===== CARTE ===== */
                .form-card { overflow: hidden; }
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 16px !important;
                    border-top-right-radius: 16px !important;
                    padding: 1.1rem 1.6rem;
                    border: none;
                }
                .header-pill {
                    background: rgba(255,255,255,.18); color: white;
                    padding: 0.35rem 0.9rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }

                /* ===== SECTIONS (bloc encadré) ===== */
                .section-block {
                    background: #fbfcfe;
                    border: 1px solid #eef2f7;
                    border-radius: 14px;
                    padding: 1.1rem 1.2rem 1.3rem;
                }
                .section-title-charte {
                    display: flex; align-items: center; gap: 0.75rem;
                    margin-bottom: 1rem; padding-bottom: 0.5rem;
                    border-bottom: 1px dashed #e2e8f0;
                }
                .section-title-charte h6 {
                    margin: 0; font-weight: 700;
                    font-size: 0.95rem; color: #1e293b;
                    letter-spacing: .2px;
                }
                .section-icon-charte {
                    width: 36px; height: 36px; border-radius: 11px;
                    display: inline-flex; align-items: center; justify-content: center;
                    font-size: 1.05rem;
                }

                /* ===== FORMULAIRES ===== */
                .form-label-modern {
                    font-weight: 600; color: #334155;
                    font-size: 0.82rem; margin-bottom: 0.4rem;
                    letter-spacing: .1px;
                }
                .input-modern .input-group-text {
                    background: #f8fafc; border: 2px solid #e2e8f0;
                    border-right: none; color: var(--primary);
                    border-radius: 10px 0 0 10px;
                    min-width: 42px; justify-content: center;
                }
                .input-modern .form-control,
                .input-modern .form-select {
                    border: 2px solid #e2e8f0; border-left: none;
                    border-radius: 0 10px 10px 0;
                    padding: 0.6rem 0.9rem; font-size: 0.9rem;
                    transition: all 0.2s ease;
                    background-color: white;
                }
                .input-modern .form-control:hover,
                .input-modern .form-select:hover {
                    border-color: #cbd5e1;
                }
                .input-modern .form-control:focus,
                .input-modern .form-select:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.12);
                }
                .input-modern .input-group-text.bg-light {
                    background: #f1f5f9 !important;
                    border-left: none;
                    border-radius: 0 10px 10px 0;
                    color: #64748b; font-weight: 600; font-size: 0.8rem;
                    padding: 0 .7rem;
                }

                /* ===== MARGE BOX ===== */
                .marge-box-charte {
                    background: #f8fafc;
                    border: 2px solid #e2e8f0;
                    border-radius: 10px;
                    padding: 0.6rem 0.9rem;
                    text-align: center;
                    font-size: 1rem;
                    height: 100%;
                    min-height: 44px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                /* ===== SÉLECTEUR DE TYPE ===== */
                .type-selector-charte {
                    display: flex; gap: 1rem; flex-wrap: wrap;
                }
                .type-btn {
                    flex: 1; min-width: 220px;
                    position: relative;
                    background: white; border: 2px solid #e2e8f0;
                    border-radius: 14px; padding: 1.1rem 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    text-align: left; cursor: pointer;
                    transition: all .25s ease;
                }
                .type-btn-icon {
                    width: 48px; height: 48px; flex-shrink: 0;
                    background: #f1f5f9; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    transition: all .25s ease;
                }
                .type-btn-icon i { font-size: 1.5rem; color: #94a3b8; }
                .type-btn-content { display: flex; flex-direction: column; }
                .type-btn-content span { font-weight: 700; color: #1e293b; font-size: 1rem; }
                .type-btn-content small { color: #64748b; font-size: .78rem; margin-top: .15rem; }
                .type-btn-check {
                    position: absolute; top: 10px; right: 12px;
                    color: var(--primary); font-size: 1.2rem;
                    animation: popIn .25s ease-out;
                }
                .type-btn:hover {
                    border-color: var(--secondary);
                    transform: translateY(-1px);
                    box-shadow: 0 6px 16px rgba(33,172,205,.10);
                }
                .type-btn.active {
                    border-color: var(--primary);
                    background: linear-gradient(135deg, #f0f4fb, #e8f7fc);
                    box-shadow: 0 6px 16px rgba(41,90,167,.15);
                }
                .type-btn.active .type-btn-icon { background: var(--primary); }
                .type-btn.active .type-btn-icon i { color: white; }
                .type-btn.active .type-btn-content span { color: var(--primary); }

                @keyframes popIn {
                    from { opacity: 0; transform: scale(.5); }
                    to { opacity: 1; transform: scale(1); }
                }

                /* ===== BOX INFO SERVICE ===== */
                .service-info-box {
                    display: flex; gap: .9rem; align-items: flex-start;
                    background: linear-gradient(135deg, #f0fbff, #e8f7fc);
                    border-left: 4px solid var(--secondary);
                    padding: 1.1rem 1.3rem; border-radius: 12px;
                    animation: fadeIn .35s ease;
                }
                .service-info-box i { color: var(--secondary); font-size: 1.4rem; margin-top: 2px; }

                /* ===== BARRE D'ACTIONS ===== */
                .action-bar {
                    display: flex; justify-content: flex-end; gap: .8rem;
                    flex-wrap: wrap;
                    padding-top: 1rem;
                    border-top: 1px dashed #e2e8f0;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.55rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white; padding: 0.55rem 1.7rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-primary:disabled { opacity: 0.6; transform: none; }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(6px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .fade-in-form { animation: fadeIn .35s ease; }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 991px) {
                    .type-btn { min-width: 100%; }
                }
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 46px; height: 46px; font-size: 1.25rem; }
                    .header-title { font-size: 1.1rem; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                    .action-bar { justify-content: stretch; }
                    .action-bar .btn-modern-outline,
                    .action-bar .btn-modern-primary { flex: 1; justify-content: center; }
                }
                .rounded-4 { border-radius: 16px !important; }
            `}</style>
        </>
    );
}